<template>
  <v-col class="pa-6">
    <div v-if="!viewingActivity">
      <v-row>
        <v-col cols="12" md="4">
          <v-text-field
            class="input"
            label="Search..."
            prepend-inner-icon="mdi-magnify"
            rounded
            v-model="search"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <v-row class="ma-0" justify="center" justify-md="start">
            <v-select
              class="input"
              style="max-width: 300px"
              label="Consultation Type"
              rounded
              v-model="selectedConsultationType"
              :items="consultationTypeOptions"
            ></v-select>
          </v-row>
        </v-col>
      </v-row>
      <v-row justify="center">
        <v-data-table
          no-data-text="No activities yet"
          style="width: 100%"
          :headers="headers"
          :items="filteredActivity"
          :items-per-page="10"
          class="elevation-1 ml-4 mr-8"
        >
          <template v-slot:item.providerFullName="{ item }">
            <h4>{{ item.providerFullName }}</h4>
            <p class="grey-color">{{ item.providerType }}</p>
          </template>
          <template v-slot:item.action="{ item }">
            <td>
              <v-icon color="#4390c0" @click="viewActivity(item)">mdi-eye</v-icon>
            </td>
          </template>
        </v-data-table>
      </v-row>
    </div>
    <div v-if="viewingActivity">
      <ViewActivity :activity="selectedActivity" @close="handleClose()" />
    </div>
  </v-col>
</template>

<script>
import ViewActivity from "@/components/ViewActivity.vue";

import {
  collection,
  getDocs,
  getFirestore,
  query,
  onSnapshot,
} from "firebase/firestore";
import { firebaseApp } from "../../firebaseConfig";
const db = getFirestore(firebaseApp);

export default {
  data() {
    return {
      headers: [
        { text: "Date", value: "date" },
        { text: "Time", value: "startTimeFormatted" },
        { text: "Client", value: "clientFullName" },
        { text: "Patients", value: "patientName" },
        { text: "Provider", value: "providerFullName" },
        { text: "Type", value: "consultType" },
        { text: "Consult Code", value: "consultCode" },
        { text: "Action", value: "action" },
      ],
      viewingActivity: false,
      activities: [],
      clients: [],
      doctors: [],
      businesses: [],
      admins: [],
      selectedActivity: null,
      search: "",
      selectedConsultationType: "All",
      consultationTypeOptions: ["All", "Normal", "Ambulance", "Other"],
      loading: false,
      unsubscribeActivities: null,
    };
  },
  components: {
    ViewActivity,
  },
  async created() {
    await this.getClients();
    await this.getDoctors();
    await this.getBusinesses();
    await this.getAdmins();
    await this.getActivities();

    if (localStorage.getItem("ViewedActivity")) {
      const selectedActivity = JSON.parse(localStorage.getItem("ViewedActivity"));
      const newActivity = this.activities.find(
        (activity) => activity.id == selectedActivity.id
      );

      if (newActivity) {
        this.selectedActivity = newActivity;
        this.viewingActivity = true;
      }
    }
  },
  methods: {
    handleClose() {
      this.viewingActivity = false;
      localStorage.removeItem("ViewedActivity");
    },
    getActivities() {
      try {
        this.loading = true;

        const q = query(collection(db, "consultations"));

        this.unsubscribeActivities = onSnapshot(q, (querySnapshot) => {
          this.activities = [];

          const clientMap = Object.fromEntries(this.clients.map((c) => [c.id, c]));
          const doctorMap = Object.fromEntries(this.doctors.map((d) => [d.id, d]));
          const businessMap = Object.fromEntries(
            this.businesses.map((business) => [business.id, business])
          );

          querySnapshot.forEach((doc) => {
            const tempActivity = doc.data();
            tempActivity.id = doc.id;
            tempActivity.consultCode = tempActivity.consultCode || "N/A";

            this.attachClient(tempActivity, clientMap);
            this.attachPatient(tempActivity);
            this.attachProvider(tempActivity, doctorMap, businessMap);
            this.attachDisputes(tempActivity);
            this.attachFees(tempActivity);

            this.activities.push(tempActivity);
          });

          this.loading = false;
        });
      } catch (error) {
        this.loading = false;
        console.log("error: ", error);
      }
    },
    attachClient(activity, clientMap) {
      const client = clientMap[activity.clientID];

      if (client) {
        activity.client = client;
        activity.clientFullName = this.formatPersonName(client, [
          "firstName",
          "lastName",
        ]);
      } else {
        activity.clientFullName = activity.clientFullName || "Client not found";
      }
    },
    attachPatient(activity) {
      if (activity.patient && !activity.patient.type) {
        activity.patientName = this.formatPersonName(activity.patient, [
          "firstName",
          "lastName",
        ]);
      } else if (activity.patient) {
        activity.patientName = activity.patient.name || "Patient not found";
      } else {
        activity.patientName = "Patient not found";
      }
    },
    attachProvider(activity, doctorMap, businessMap) {
      const providerInfo = this.resolveProvider(activity, doctorMap, businessMap);

      activity.provider = providerInfo.provider;
      activity.providerID = providerInfo.id;
      activity.providerFullName = providerInfo.name;
      activity.providerEmail = providerInfo.email;
      activity.providerPhone = providerInfo.phone;
      activity.providerType = providerInfo.type;

      activity.doctor = providerInfo.provider;
      activity.doctorFullName = providerInfo.name;
      activity.doctorEmail = providerInfo.email;
      activity.doctorPhone = providerInfo.phone;
      activity.doctorType = providerInfo.type;
    },
    resolveProvider(activity, doctorMap, businessMap) {
      const providerId =
        activity.doctorID ||
        activity.nurseID ||
        activity.ambulanceID ||
        activity.providerID ||
        activity.businessID;
      const provider = doctorMap[providerId] || businessMap[providerId] || null;
      const isAmbulance = !!activity.ambulanceID || this.hasText(activity.consultType, "ambulance");
      const isNurse = !!activity.nurseID || this.hasText(activity.consultType, "nurse");
      const type = this.resolveProviderType(activity, provider, isAmbulance, isNurse);

      return {
        id: providerId,
        provider,
        name: this.resolveProviderName(activity, provider, isAmbulance, isNurse),
        email: provider ? provider.email || "N/A" : activity.providerEmail || activity.doctorEmail || "N/A",
        phone: provider
          ? provider.CellNo || provider.contact || "N/A"
          : activity.providerPhone || activity.doctorPhone || activity.CellNo || "N/A",
        type,
      };
    },
    resolveProviderName(activity, provider, isAmbulance, isNurse) {
      if (provider) {
        return (
          provider.companyName ||
          this.formatPersonName(provider, ["name", "surname"]) ||
          this.formatPersonName(provider, ["firstName", "lastName"]) ||
          "Provider not found"
        );
      }

      return (
        activity.providerFullName ||
        activity.providerName ||
        activity.doctorFullName ||
        activity.doctorName ||
        activity.nurseFullName ||
        activity.nurseName ||
        activity.ambulanceName ||
        activity.companyName ||
        (isAmbulance ? "Ambulance provider not found" : isNurse ? "Nurse not found" : "Provider not found")
      );
    },
    resolveProviderType(activity, provider, isAmbulance, isNurse) {
      if (isAmbulance) {
        return "Ambulance";
      }

      if (isNurse) {
        return "Nurse";
      }

      const profession = provider && provider.profession;
      if (Array.isArray(profession)) {
        return profession.join(", ") || "Doctor";
      }

      return (
        activity.providerType ||
        activity.doctorType ||
        profession ||
        (provider && provider.userType) ||
        "Doctor"
      );
    },
    attachDisputes(activity) {
      if (activity.disputes) {
        activity.disputes.forEach((dispute) => {
          if (dispute.userType === "Admin") {
            dispute.admin = this.admins.find((admin) => admin.id === dispute.userID);
          }
        });
      }
    },
    attachFees(activity) {
      const paidAmount = activity.paidAmount || 0;
      const lotusFee = (paidAmount / 100) * 25;
      const providerAmount = paidAmount - lotusFee;
      let additionalAmount = 0;

      if (activity.additionalCharges && activity.additionalCharges.length > 0) {
        activity.additionalCharges.forEach((charge) => {
          additionalAmount += charge.amount || 0;
        });
      }

      const lotusAdditionalFee = (additionalAmount / 100) * 25;
      const providerAdditionalAmount = additionalAmount - lotusAdditionalFee;

      activity.lotusFee = lotusFee;
      activity.doctorAmount = providerAmount;
      activity.providerAmount = providerAmount;
      activity.totalAdditionalAmount = additionalAmount;
      activity.lotusAdditionalFee = lotusAdditionalFee;
      activity.doctorAdditionalAmount = providerAdditionalAmount;
      activity.providerAdditionalAmount = providerAdditionalAmount;
    },
    formatPersonName(item, fields) {
      const name = fields.map((field) => item[field]).filter(Boolean).join(" ").trim();
      return name || "";
    },
    hasText(value, text) {
      return value && value.toString().toLowerCase().includes(text);
    },
    async getDoctors() {
      try {
        const q = query(collection(db, "doctors"));

        this.loading = true;
        const querySnapshot = await getDocs(q);
        this.loading = false;

        this.doctors = [];

        querySnapshot.docs.forEach((doc) => {
          const tempDoctor = doc.data();
          tempDoctor.id = doc.id;

          this.doctors.push(tempDoctor);
        });
      } catch (error) {
        this.loading = false;
        console.log("error: ", error);
      }
    },
    async getBusinesses() {
      try {
        const q = query(collection(db, "businesses"));

        this.loading = true;
        const querySnapshot = await getDocs(q);
        this.loading = false;

        this.businesses = [];

        querySnapshot.docs.forEach((doc) => {
          const tempBusiness = doc.data();
          tempBusiness.id = doc.id;

          this.businesses.push(tempBusiness);
        });
      } catch (error) {
        this.loading = false;
        console.log("error: ", error);
      }
    },
    async getClients() {
      try {
        const q = query(collection(db, "clients"));

        this.loading = true;
        const querySnapshot = await getDocs(q);
        this.loading = false;

        this.clients = [];

        querySnapshot.docs.forEach((doc) => {
          const tempClient = doc.data();
          tempClient.id = doc.id;

          this.clients.push(tempClient);
        });
      } catch (error) {
        this.loading = false;
        console.log("error: ", error);
      }
    },
    async getAdmins() {
      try {
        const q = query(collection(db, "dashboardUsers"));

        this.loading = true;
        const querySnapshot = await getDocs(q);
        this.loading = false;

        this.admins = [];

        querySnapshot.docs.forEach((doc) => {
          const tempAdmin = doc.data();
          tempAdmin.id = doc.id;

          this.admins.push(tempAdmin);
        });
      } catch (error) {
        this.loading = false;
        console.log("error: ", error);
      }
    },
    consultationCategory(activity) {
      const providerName = activity.providerFullName || activity.doctorFullName || "";

      if (
        activity.useAlternativeBooking ||
        this.hasText(providerName, "provider not found") ||
        this.hasText(providerName, "other")
      ) {
        return "Other";
      }

      if (
        activity.ambulanceID ||
        this.hasText(activity.consultType, "ambulance") ||
        this.hasText(activity.providerType, "ambulance")
      ) {
        return "Ambulance";
      }

      return "Normal";
    },
    matchesConsultationType(activity) {
      return (
        this.selectedConsultationType == "All" ||
        this.consultationCategory(activity) == this.selectedConsultationType
      );
    },
    viewActivity(activity) {
      this.selectedActivity = activity;
      this.viewingActivity = true;
    },
  },
  computed: {
    filteredActivity() {
      return this.activities
        .filter((item) => {
          if (!this.matchesConsultationType(item)) {
            return false;
          }

          return Object.values(item).some((value) => {
            if (value !== null && value !== undefined) {
              return value
                .toString()
                .toLowerCase()
                .includes(this.search.toLowerCase());
            }
            return false;
          });
        })
        .sort((a, b) => {
          const dateA = new Date(`${a.date}T${a.startTime}`);
          const dateB = new Date(`${b.date}T${b.startTime}`);
          return dateB - dateA;
        });
    },
  },
  beforeUnmount() {
    if (this.unsubscribeActivities) {
      this.unsubscribeActivities();
    }
  },
};
</script>

<style scoped></style>